"use client";

import * as React from "react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components-shared/ui/button";
import { dir } from "i18next";
import { useLocale } from "next-intl";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  initialDate?: Date;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  initialDate,
  ...props
}: CalendarProps) {
  const locale = useLocale();
  return (
    <DayPicker
      dir={dir(locale)}
      showOutsideDays={showOutsideDays}
      defaultMonth={initialDate}
      className={cn("p-3", className)}
      classNames={{
        months:
          "flex flex-col relative sm:flex-row gap-y-4 sm:gap-x-4 sm:gap-y-0",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "flex justify-between w-full absolute top-1",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 z-10 bg-transparent p-0 opacity-50 hover:opacity-100 rtl:rotate-180"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "size-7 z-10 bg-transparent p-0 opacity-50 hover:opacity-100 rtl:rotate-180"
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex gap-x-1",
        weekday:
          "text-neutral-500 rounded-md w-10 font-normal text-[0.7rem] dark:text-neutral-400",
        week: "flex w-full mt-2 gap-x-1",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "font-normal aria-selected:opacity-100 !rounded size-10 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-neutral-100/50 [&:has([aria-selected])]:bg-neutral-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 dark:[&:has([aria-selected].day-outside)]:bg-neutral-800/50 dark:[&:has([aria-selected])]:bg-neutral-800"
        ),
        day_button: "size-full border rounded",
        selected:
          "bg-green-main text-neutral-50 hover:!bg-green-main/90 hover:text-neutral-50 focus:bg-green-main focus:text-neutral-50 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-50 dark:hover:text-neutral-900 dark:focus:bg-neutral-50 dark:focus:text-neutral-900",
        today: "border border-black dark:bg-neutral-800 dark:text-neutral-50",
        outside:
          "day-outside text-neutral-500 opacity-50 aria-selected:bg-neutral-100/50 aria-selected:text-neutral-500 aria-selected:opacity-30 dark:text-neutral-400 dark:aria-selected:bg-neutral-800/50 dark:aria-selected:text-neutral-400",
        disabled: "text-neutral-500 opacity-50 dark:text-neutral-400",
        range_middle:
          "aria-selected:!bg-green-main/70 aria-selected:text-white dark:aria-selected:bg-neutral-800 dark:aria-selected:text-neutral-50",
        hidden: "invisible",
        range_start: "!bg-green-main !text-white",
        range_end: "!bg-green-main !text-white",
        ...classNames,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
